import { createFileRoute } from "@tanstack/react-router";
import { EmployeeStatusColors } from "@/components/employee/employee-meta";
import SimpleTable from "@/components/ui/table";
import { LoginHistoryTableColumns } from "@/components/user/user-meta";
import { useTranslate } from "@/hooks/use-translate";
import { cn } from "@/lib/utils";
import useAppStore from "@/store";
import type { Employee } from "@/store/Employee";
import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/(dasboard)/employees/$id/view")({
  component: ViewEmployeePage,
});

function ViewEmployeePage() {
  const store = useAppStore();
  const { t, formatDate } = useTranslate();
  const employee = store.employee.current as Employee | null;
  const params = Route.useParams();

  useEffect(() => {
    store.employee.fetchOne(params.id);
  }, [params.id]);

  if (!employee) return null;

  const details = [
    { label: t("First Name"), value: employee.user.first_name },
    { label: t("Last Name"), value: employee.user.last_name },
    { label: t("Email"), value: employee.user.email },
    { label: t("Phone"), value: employee.user.phone_number },
    { label: t("Date of Birth"), value: formatDate(employee.date_of_birth) },

    {
      label: t("Status"),
      value: (
        <span
          className={cn(
            "rounded-full px-2 py-1 text-sm",
            EmployeeStatusColors[employee.status]
          )}
        >
          {t(employee.status)}
        </span>
      ),
    },
    { label: t("Description"), value: employee.description || "—" },
  ];

  const employmentDetails = [
    { label: t("Hire Date"), value: formatDate(employee.hire_date) },
    { label: t("Employment Status"), value: employee.employment_status },
    { label: t("Role"), value: employee.user.role },
    { label: t("Emergency Contact"), value: employee.emergency_contact },
    { label: t("Salary"), value: employee.salary },
  ];

  const metadataDetails = [
    { label: t("Created At"), value: formatDate(employee.created_at) },
    { label: t("Updated At"), value: formatDate(employee.updated_at) },
  ];

  return (
    <section className="max-w-5xl mx-auto">
      <div>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:underline font-semibold"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="">{t("Back")}</span>
        </button>
      </div>
      <div className="mt-4 flex justify-between mb-4">
        <h2 className="text-xl font-semibold">{t("View employee")}</h2>
      </div>
      <div className="bg-white p-6 text-sm">
        {Object.entries({
          [t("General Information")]: details,
          [t("Employment Details")]: employmentDetails,
          [t("Metadata")]: metadataDetails,
        }).map(([title, details]) => (
          <section
            key={title}
            className="bg-gray-50 border-b border-gray-300 border-dashed overflow-hidden"
          >
            <h3 className="font-semibold text-gray-800 p-2 px-4 bg-gray-100">
              {title}
            </h3>
            <ul className="grid grid-cols-2 gap-y-4 px-4 py-2">
              {details.map((detail) => (
                <li key={detail.label}>
                  <div className="text-gray-600">{detail.label}</div>
                  <div className="font-medium mt-1">{detail.value}</div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <header className="mt-6">
        <h2 className="font-medium">{t("Login history")}</h2>
      </header>
      <aside className="mt-2">
        <SimpleTable
          items={employee.user.login_histories}
          columns={LoginHistoryTableColumns({})}
        />
      </aside>
    </section>
  );
}
